/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { Formik } from 'formik'
import styled from 'styled-components'
import TextArea from '@mdsol/lego/lib/TextArea'
import { Select } from '../../../components/Select/Select'
import { ErrorMessageStyled } from '../../../components/shared/ErrorMessage'
import Validation from '../../../lib/Validation'
import Utils from '../../../lib/Utils'
import { WorkflowProcessingType } from '../../../constants/workflow'
import useNumericOnly from '../../../hooks/useNumberOnly'
import { useServiceContext } from '../../../root'
import _ from 'lodash'
import { createQCReviewByFilters, createQCReviewByIds } from '../../../services/api/etmf'
import { toast } from '../../../services/toast'
import Modal from '~/components/Modal'

export const UserStepWrapper = styled.div`
    display: flex;
    position: relative;
    padding: 4px 8px;
    margin: 10px 0;
    background: #f6f6f6;
    border: 1px solid #d3d4d7;
    color: #333333;
    border-radius: 3px;
    align-items: flex-start;
    .nofall-wrapper {
        font-size: 12px;
        line-height: 25px;
        margin-bottom: 5px;

        .nofall-input {
            width: 26px;
            height: 25px;
            border: '1px solid #ccc',
            border-radius: '2px'
            line-height: 25px;
            margin-right: 5px;
            padding-left: 5px;
        }
    }
`

export const DocumentsListWrapper = styled.div`
  font-size: 12px;
  width: 100%;
  padding: 6px 6px 0px 6px;
  color: #333333;
  line-height: 14px;
  height: 150px;
  border: 1px solid #cccccc;
  overflow-y: auto;
  border-radius: 3px;
  box-shadow: inset 1px 2px 3px 0 #efefef;
  ul {
    padding-left: 0px;
    margin-bottom: 0px;
    list-style-type: none;

    li {
      display: inline-block;
      overflow: hidden;
      width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      padding-bottom: 2px;
    }
  }
`

interface TaskTeam {
  group?: { id: string }
  user?: { id: string }
}

export function createTaskTeams(arr: any[]): TaskTeam[] {
  return (
    arr?.reduce((acc: TaskTeam[], item: any) => {
      const stepItemArr = item?.value ? item?.value?.split('-') : item?.split('-')
      Array.isArray(stepItemArr) && stepItemArr?.length === 2
        ? acc.push(
            stepItemArr[0] === 'GROUP'
              ? {
                  group: { id: stepItemArr[1] }
                }
              : {
                  user: { id: stepItemArr[1] }
                }
          )
        : null
      return acc
    }, []) || []
  )
}

interface ProcessingTypeValue {
  processingType: string
  nofAllValue: string | number
}

interface FormValues {
  description: string
  reviewers: any[] | null
  approvers: any[] | null
  rev: ProcessingTypeValue
  apr: ProcessingTypeValue
}

interface FormErrors {
  description?: string
  reviewers?: string
  approvers?: string
  rev?: {
    nofAllValue?: string
  }
  apr?: {
    nofAllValue?: string
  }
}

interface SelectedItem {
  id: string | number
  name: string
}

interface CreateQCReviewModalOptions {
  selectedIds?: (string | number)[]
  selectedWholeResult?: boolean
  totalItemsInSection?: number
  selectedItems?: SelectedItem[]
  filter?: any
  parentFolderId?: string | number
  isPropertiesSearch?: boolean
}

interface CreateQCReviewModalProps {
  fsi: any
  options: CreateQCReviewModalOptions
  onClose?: () => void
}

const CreateQCReviewModal: React.FC<CreateQCReviewModalProps> = ({ fsi, options, onClose }) => {
  const { selectedIds = [], selectedWholeResult, totalItemsInSection, selectedItems = [] } = options
  const context = useServiceContext()
  const numInputRef = useRef<HTMLInputElement>(null)
  useNumericOnly(numInputRef, 9)
  const numInputRefAppr = useRef<HTMLInputElement>(null)
  useNumericOnly(numInputRefAppr, 9)
  const { constant: Constants, api, $rootScope } = context
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const [docCount, setDocCount] = useState<number>()
  const [documentList, setDocumentList] = useState<React.ReactNode>()
  const filter: FormValues = {
    description: translate('QC_REVIEW_DESCRIPTION'),
    reviewers: null,
    approvers: null,
    rev: {
      processingType: WorkflowProcessingType.N_OF_ALL,
      nofAllValue: ''
    },
    apr: {
      processingType: WorkflowProcessingType.N_OF_ALL,
      nofAllValue: ''
    }
  }
  const { qcReview: { qcReviewMaxDoc, qcReviewEnabled } = { qcReviewMaxDoc: null, qcReviewEnabled: false } } =
    context.service.init.getFeaturesInfo()

  useEffect(() => {
    if (qcReviewEnabled) {
      fetchUsers()
      const { element, count } = getDocumentsListForQC()

      if (
        (qcReviewMaxDoc && selectedIds?.length > qcReviewMaxDoc) ||
        (selectedWholeResult && totalItemsInSection && qcReviewMaxDoc && totalItemsInSection > qcReviewMaxDoc)
      ) {
        toast.error(translate('QC_REVIEW_MAX_DOC_LIMIT_ERROR_MSG', { maxDocLimit: qcReviewMaxDoc }))
      } else {
        console.log('element', element)
        setDocumentList(element)
        setDocCount(count)
        setIsOpen(true)
      }
    }
  }, [])

  function fetchUsers() {
    context.api.users.getAssembledUsersGroups().then((data: any) => {
      const usersFiltered = _.chain(data?.merged)
        .map((user: any) => {
          user.label = user.chName
          return {
            value: user?._uid,
            label: user?.chName,
            id: user?.id
          }
        })
        .value()
      setUsers(usersFiltered)
    })
  }

  function getDocumentsListForQC() {
    let element: React.ReactNode
    let count: number = 0

    if (selectedItems?.length > 1 && !selectedWholeResult) {
      count = selectedItems.length
      element = (
        <DocumentsListWrapper>
          <ul>
            {selectedItems.map((item: SelectedItem) => (
              <li title={item?.name} key={item?.id}>
                {item?.name}
              </li>
            ))}
          </ul>
        </DocumentsListWrapper>
      )
    } else if (selectedWholeResult && selectedIds?.length > 1) {
      count = totalItemsInSection || 0
      element = <p title={translate('WORKFLOW_MULTIPLE_DOCS')}>{translate('WORKFLOW_MULTIPLE_DOCS')}</p>
    } else if ((!selectedWholeResult && selectedIds?.length === 1) || selectedIds?.length === 0) {
      count = 1
      element = <p title={fsi?.name}>{fsi?.name ? fsi.name : ''}</p>
    }

    return {
      element,
      count
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function submitForm(values: FormValues) {
    const description = values?.description
    const rev_nofAllValue = values.rev?.nofAllValue || null
    const apr_nofAllValue = values.apr?.nofAllValue || null
    const workflowType = Constants.workflow.type.QC_REVIEW

    const commonDTO = {
      startMultiCreateQCReviewWorkflowRequestDTO: {
        description,
        workflowType,
        reviewers:
          values?.reviewers?.length || rev_nofAllValue
            ? {
                processingType: Constants.workflow.processingType.N_OF_ALL,
                nofAllValue: rev_nofAllValue,
                taskTeamStepLabel: null,
                taskTeams: createTaskTeams(values?.reviewers || [])
              }
            : null,
        approvers: {
          processingType: Constants.workflow.processingType.N_OF_ALL,
          nofAllValue: apr_nofAllValue,
          taskTeamStepLabel: null,
          taskTeams: createTaskTeams(values?.approvers || [])
        }
      }
    }
    let apiCall: Promise<any>

    if (selectedWholeResult) {
      options?.isPropertiesSearch ? null : (options.filter.folderId = options?.parentFolderId)
      const subDto = {
        propertySearchRequestDTO: {
          ...(Object.keys(options?.filter)?.length
            ? options?.filter?.metadataCriteria
              ? { filter: options.filter }
              : {
                  filter: options?.isPropertiesSearch
                    ? {
                        ...options.filter,
                        metadataCriteria: []
                      }
                    : {
                        ...options.filter,
                        includeSubFolders: false,
                        metadataCriteria: []
                      }
                }
            : {})
        }
      }
      apiCall = createQCReviewByFilters({ ...commonDTO, ...subDto })
    } else {
      const subDto = {
        ids: selectedIds?.length ? selectedIds : [fsi?.id]
      }
      apiCall = createQCReviewByIds({ ...commonDTO, ...subDto })
    }

    apiCall.then(
      () => {
        toast.success(translate('CREATE_QC_REVIEW_SUCCESS_MES'))
        window.dispatchEvent(new CustomEvent('updateFiles'))
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
      },
      (error: any) => {
        toast.error(error?.data?.message)
      }
    )
  }

  return (
    !!isOpen && (
      <Modal className="theme-sandman" title={translate('CREATE_QC_REVIEW')} show={isOpen} handleClose={doClose}>
        <Formik
          validateOnChange
          validate={(values: FormValues) => {
            const errors: FormErrors = {}
            const hasReviewers = _.isArray(values.reviewers) && values.reviewers.length > 0
            const hasApprovers = _.isArray(values.approvers) && values.approvers.length > 0
            const reviewerCount = values?.rev?.nofAllValue
            const approverCount = values?.apr?.nofAllValue

            const validateNofAllValue = (pathPrefix: 'rev' | 'apr') => {
              const targetValue = values?.[pathPrefix]?.nofAllValue
              const isValid = Number.isInteger(targetValue) && Number(targetValue) >= 1
              return isValid
            }

            // Helper function to validate phase with users but no count
            const validateUsersWithoutCount = (
              hasUsers: boolean,
              count: string | number,
              phase: 'rev' | 'apr',
              errorMsg: string
            ) => {
              if (hasUsers && (count == null || count === '')) {
                errors[phase] = errors[phase] || {}
                errors[phase]!.nofAllValue = errorMsg
              }
            }

            // Helper function to validate phase with users and count
            const validateUsersWithCount = (hasUsers: boolean, count: string | number, phase: 'rev' | 'apr') => {
              if (hasUsers && count != null && count !== '') {
                if (!validateNofAllValue(phase)) {
                  errors[phase] = errors[phase] || {}
                  errors[phase]!.nofAllValue = translate('SOME_AT_ONCE_ERROR_MSG')
                }
              }
            }

            if (!values?.description?.trim()) {
              errors.description = Validation.general.mixed.required
            }
            // QC Reviewers validation
            validateUsersWithoutCount(hasReviewers, reviewerCount, 'rev', translate('SOME_AT_ONCE_ERROR_MSG'))
            validateUsersWithCount(hasReviewers, reviewerCount, 'rev')

            // QC Approvers validation
            if (!hasApprovers && (approverCount == null || approverCount === '')) {
              errors.approvers = translate('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
            }
            validateUsersWithoutCount(hasApprovers, approverCount, 'apr', translate('SOME_AT_ONCE_ERROR_MSG'))
            validateUsersWithCount(hasApprovers, approverCount, 'apr')
            return errors
          }}
          enableReinitialize={true}
          innerRef={(el: any) => {
            context.store.formRef = el
          }}
          initialValues={filter}
          onSubmit={(values: FormValues) => {
            submitForm(values)
            doClose()
          }}
        >
          {({ values, setFieldValue, handleSubmit, setFieldTouched }) => (
            <div className="qcReviewModal">
              <Modal.Body>
                <div className="form-group md-form-group text-area-wrapper">
                  <label htmlFor="qc-desc">{translate('BROWSER_SEND_RQST_REASON_LABEL')}</label>
                  <TextArea
                    {...({ maxLength: 255 } as any)}
                    name="description"
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const { value } = event.target
                      setFieldValue('description', value)
                    }}
                    id="qc-desc"
                    value={values?.description}
                    rows={4}
                  />
                  <ErrorMessageStyled name="description" classesName="fs12" />
                </div>
                <div className="form-group">
                  <label htmlFor="documents">
                    {`
                                        ${translate('DOCUMENT(S)')}
                                        (${docCount} ${translate('ETMF_GRAPHS_TOTAL')})
                                    `}
                  </label>
                  {documentList}
                </div>
                <div className="form-group">
                  <label htmlFor="reviewers">{`${translate('QC_REVIEW_SEQUENCE')} (${translate('OPTIONAL_PROPERTIES')})`}</label>
                  <UserStepWrapper>
                    <div className="w50p">
                      <Select
                        placeholder={`${translate('SELECT_USERS/GROUPS...')}`}
                        name="reviewers"
                        className="fs12"
                        options={users}
                        onChange={(value: any) => {
                          setFieldValue('reviewers', value)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        multi={true}
                        id="3b3b6ccd-ae67-4i06-a399-dfef4d0ec5db"
                      />
                      <ErrorMessageStyled name="reviewers" classesName="fs12" />
                    </div>
                    <ProcessingType
                      values={values}
                      phase="rev"
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      numInputRef={numInputRef}
                      translate={translate}
                    />
                  </UserStepWrapper>
                </div>
                <div className="form-group">
                  <label htmlFor="approvers">{`${translate('QC_APPROVAL_SEQUENCE')}`}</label>
                  <UserStepWrapper>
                    <div className="w50p">
                      <Select
                        placeholder={`${translate('SELECT_USERS/GROUPS...')}`}
                        name="approvers"
                        className="fs12"
                        options={users}
                        onChange={(value: any) => {
                          setFieldValue('approvers', value)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        multi={true}
                        id="4b4e6cck-ae67-4i06-a399-dfef4d9ec5db"
                      />
                      <ErrorMessageStyled name="approvers" classesName="fs12" />
                    </div>
                    <ProcessingType
                      values={values}
                      phase="apr"
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      numInputRef={numInputRefAppr}
                      translate={translate}
                    />
                  </UserStepWrapper>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  data-action={'cancel'}
                  variant="link"
                  onClick={doClose}
                  id="q9a0g80-283a-4f36-9b08-704a90ee08bf"
                >
                  {translate('CANCEL')}
                </Button>
                <Button
                  data-action={'ok'}
                  className="btn btn-primary"
                  onClick={(e: any) => handleSubmit(e)}
                  id="40a86k-d85c-4330-bdb7-ba0k6ad6535c"
                >
                  {translate('CREATE_QC_REVIEW')}
                </Button>
              </Modal.Footer>
            </div>
          )}
        </Formik>
      </Modal>
    )
  )
}

export default CreateQCReviewModal

interface ProcessingTypeProps {
  values: FormValues
  phase: 'rev' | 'apr'
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
  numInputRef: React.RefObject<HTMLInputElement | null>
  translate: (key: string, params?: any) => string
}

export function ProcessingType({
  values,
  phase,
  setFieldValue,
  setFieldTouched,
  numInputRef,
  translate
}: ProcessingTypeProps) {
  const inputValue = values?.[phase]?.nofAllValue != null ? values?.[phase]?.nofAllValue : ''
  return (
    <span className="ml10 w50p">
      <div className="nofall-wrapper hform-group pt5">
        <input
          ref={numInputRef}
          type="number"
          name={`${phase}.nofAllValue`}
          value={inputValue}
          className="md-input nofall-input"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value === '' ? '' : Number(e.target.value)
            setFieldValue(`${phase}.nofAllValue`, val)
          }}
          onBlur={() => {
            const nofAllValue = values?.[phase]?.nofAllValue
            if (nofAllValue === '' || Number(nofAllValue) <= 0) {
              setFieldTouched(`${phase}.nofAllValue`, true)
            } else {
              setFieldTouched(`${phase}.nofAllValue`, false)
            }
          }}
        />
        {phase === 'rev'
          ? translate('TASK_TEAM_N_OF_USER_NEEDED_REVIEW')
          : translate('TASK_TEAM_N_OF_USER_NEEDED_APPROVE')}
        <div className="pt5 normal-line-height">
          <ErrorMessageStyled name={`${phase}.nofAllValue`} />
        </div>
      </div>
    </span>
  )
}
